import { useTheme } from '@emotion/react'
import {
    ConsentLevel,
    isServerEnvironment,
    Product,
    ScriptLoadResult,
    useConsentGiven,
} from '@news-mono/web-common'
import React, { useCallback, useRef, useState } from 'react'
import { useProduct } from '../../__product/useProduct'
import debug from 'debug'

let hasTaboolaLoaded = false

export const TaboolaContext = React.createContext<null | {
    result: ScriptLoadResult
    flushQueue: (pageId: string) => void
    newPage: (pageId: string, command: TaboolaLocationCommand) => void
    addPlacement: (pageId: string, command: TaboolaPlacementCommand) => void
}>(null)

const taboolaProductName = {
    sevennews: '7news',
    perthnow: 'perthnow',
    thewest: 'thewest',
    thenightly: 'thenightly',
}

export function getTaboolaPublisher(product: Product, section: string) {
    const isProduction = process.env.NODE_ENV === 'production'

    if (product === 'sevennews') {
        return isProduction
            ? 'sevenwestmedia-7news'
            : 'sevenwestmedia-7newstest'
    } else if (product === 'thewest' && section !== 'default') {
        return 'sevenwestmedia-network'
    } else if (section === 'community-news' || section === 'local-news') {
        return 'sevenwestmedia-cng'
    } else {
        return `sevenwestmedia-${taboolaProductName[product]}`
    }
}

export interface TaboolaPropsScript {
    ampPosition?: number
}

export const TaboolaScriptProvider: React.FC<TaboolaPropsScript> = ({
    children,
}) => {
    const product = useProduct()
    const { section } = useTheme()
    const consentGiven = useConsentGiven({
        consentRequiredLevel: ConsentLevel.Analytics | ConsentLevel.Advertising,
    })

    // Metadata setup

    let publisher: string
    if (isServerEnvironment() || !window.taboolaPublisher) {
        // Assumption: all of the sections on the west besides default are regionals
        publisher = getTaboolaPublisher(product, section)

        if (!isServerEnvironment()) {
            window.taboolaPublisher = publisher
        }
    } else {
        publisher = window.taboolaPublisher
    }

    const [result, setResult] = useState(() =>
        consentGiven
            ? ScriptLoadResult.Loading
            : ScriptLoadResult.NotConsented,
    )
    const [error, setError] = useState<ErrorEvent | null>(null)

    const logger = debug('_taboola:script-provider')
    const flushNeeded = useRef(false)
    const lastNotifiedPage = useRef<string | null>(null)
    const pushedPlacements = useRef(new Set<string>())

    React.useEffect(() => {
        if (!consentGiven) {
            setResult(ScriptLoadResult.NotConsented)
            setError(null)
            return
        }

        const loaderUrl = `//cdn.taboola.com/libtrc/${publisher}/loader.js`
        const privacyLoaderUrl = `//static.tblcontent.com/libtrc/${publisher}/loader.privacy.js`
        const pixelUrl = `https://static.qovani.com/libtrc/tr5?type=pixel&publisher=${publisher}`
        const scriptId = 'tb_loader_script'

        window._taboola = window._taboola || []
        new Image().src = pixelUrl

        const firstScript = document.getElementsByTagName('script')[0]
        if (!firstScript?.parentNode) {
            setResult(ScriptLoadResult.Failed)
            setError(new ErrorEvent('error'))
            return
        }

        const handleSuccess = () => {
            setResult(ScriptLoadResult.Success)
            setError(null)
        }

        const handleFailure = () => {
            setResult(ScriptLoadResult.Failed)
            setError(new ErrorEvent('error'))
        }

        const injectLoader = (
            id: string,
            src: string,
            fallbackSrc: string | null,
        ) => {
            if (document.getElementById(id)) {
                handleSuccess()
                return
            }

            const script = document.createElement('script')
            script.async = true
            script.src = src
            script.id = id
            script.onload = handleSuccess
            script.onerror = () => {
                if (script.parentNode) {
                    script.parentNode.removeChild(script)
                }

                if (fallbackSrc) {
                    injectLoader(`${scriptId}_fb`, fallbackSrc, null)
                    return
                }

                handleFailure()
            }
            firstScript.parentNode?.insertBefore(script, firstScript)
        }

        setResult(ScriptLoadResult.Loading)
        setError(null)
        injectLoader(scriptId, loaderUrl, privacyLoaderUrl)

        if (
            process.env.NODE_ENV !== 'test' &&
            typeof window.performance?.mark === 'function'
        ) {
            window.performance.mark('tbl_ic')
        }
    }, [consentGiven, publisher])

    const context = {
        result,
        newPage: useCallback(
            (page: string, command: TaboolaLocationCommand) => {
                if (result !== ScriptLoadResult.Success) {
                    return
                }
                const isInitialPage = lastNotifiedPage.current === null

                const isNewPage = lastNotifiedPage.current !== page

                const shouldSendLocationCommand = isNewPage || isInitialPage

                const shouldSendNewPageCommand =
                    shouldSendLocationCommand && !isInitialPage

                if (shouldSendNewPageCommand) {
                    const command = { notify: 'newPageLoad' }
                    window._taboola.push(command)

                    pushedPlacements.current.clear()

                    logger?.('[sent] TaboolaNotifyNewPageCommand', command, {
                        page,
                    })
                } else {
                    logger?.(
                        '[skipped] TaboolaNotifyNewPageCommand',
                        {
                            reason: isInitialPage
                                ? 'not needed for initial pageload'
                                : 'not needed at this time',
                        },
                        { page },
                    )
                }

                if (shouldSendLocationCommand) {
                    window._taboola.push(command)
                    logger?.('[sent] TaboolaLocationCommand', command, { page })
                    lastNotifiedPage.current = page
                } else {
                    logger?.(
                        '[skipped] TaboolaLocationCommand',
                        { reason: 'already sent for this page' },
                        { page },
                    )
                }
            },
            [result, logger],
        ),
        addPlacement: useCallback(
            (page: string, command: TaboolaPlacementCommand) => {
                if (result !== ScriptLoadResult.Success) {
                    return
                }

                const placementKey = `${command.placement}:${command.container}`
                if (pushedPlacements.current.has(placementKey)) {
                    logger?.(
                        '[skipped] TaboolaPlacementCommand',
                        { reason: 'already pushed for this page view' },
                        { page, command },
                    )
                    return
                }

                logger?.('[sent] TaboolaPlacementCommand', command, { page })
                window._taboola.push(command)
                pushedPlacements.current.add(placementKey)
                flushNeeded.current = true
            },
            [result, logger],
        ),
        flushQueue: useCallback(
            (page: string) => {
                if (result !== ScriptLoadResult.Success) {
                    return
                }
                setTimeout(() => {
                    if (flushNeeded.current) {
                        const command = { flush: true }
                        window._taboola.push(command)
                        flushNeeded.current = false
                        logger?.('[sent] TaboolaFlushCommand', command, {
                            page,
                        })
                    } else {
                        logger?.(
                            '[skipped] TaboolaFlushCommand',
                            { reason: 'not needed' },
                            { page },
                        )
                    }
                }, (Math.random() + 0.5) * 10)
            },
            [result, logger],
        ),
    }

    React.useEffect(() => {
        if (result === ScriptLoadResult.Success) {
            if (!hasTaboolaLoaded) {
                hasTaboolaLoaded = true
            }
        }
    }, [result, error])

    return (
        <TaboolaContext.Provider value={context}>
            {children}
        </TaboolaContext.Provider>
    )
}
TaboolaScriptProvider.displayName = 'Taboola'

export default TaboolaScriptProvider
